import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useDASI } from '../../context/DASIContext';
import './styles.scss';

const BrokerResolutionDesk = () => {
  const location = useLocation();
  const { entries, setCurrentIndex } = useDASI();
  const [selectedEntryIndex, setSelectedEntryIndex] = useState(null);
  const [copyLabel, setCopyLabel] = useState('Copy to Clipboard');
  const emailRef = useRef(null);

  // Show all fully-resolved entries that have exceptions (including all-overridden)
  const entriesWithExceptions = entries
    .map((e, i) => ({ ...e, originalIndex: i }))
    .filter((e) => {
      const hasExceptions = e.exceptions.length > 0 || e.hitl.length > 0;
      if (!hasExceptions) return false;
      return (
        e.exceptions.every((ex) => ex.cleared) &&
        e.hitl.every((h) => h.resolutionMode !== null)
      );
    });

  useEffect(() => {
    if (location.state?.entryIndex != null) {
      setSelectedEntryIndex(location.state.entryIndex);
      setCurrentIndex(location.state.entryIndex);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSelectEntry = (originalIndex) => {
    setSelectedEntryIndex(originalIndex);
    setCurrentIndex(originalIndex);
  };

  const getEmailContent = (entry) => {
    let emailBodySnippets = '';
    let requiresPSC = false;

    entry.exceptions.forEach((ex) => {
      const isAccepted = ex.items.some((it) => it.resolutionMode === 'accept');
      if (isAccepted) {
        requiresPSC = true;
        emailBodySnippets += ex.emailSnippet;
      }
    });

    entry.hitl.forEach((h) => {
      if (h.resolutionMode === 'accept') {
        requiresPSC = true;
        const rawVal = h.resolutionReason.replace(/[^0-9.]/g, '');
        const parsedVal = parseFloat(rawVal);
        const fmt = isNaN(parsedVal)
          ? h.resolutionReason
          : parsedVal.toLocaleString('en-US', { minimumFractionDigits: 2 });
        emailBodySnippets += `<strong>[COMPLIANCE RISK] Undeclared Assist:</strong> A dutiable assist was omitted from the original entry. Please amend the entry to add <strong>$${fmt}</strong> to the total entered value under 19 CFR 152.103, and remit the resulting duty deficiency to CBP.<br><br>`;
      }
    });

    if (!requiresPSC) return null;

    return `<div style="border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 12px;"><strong>To:</strong> ${entry.emailTo}<br><strong>Subject:</strong> ${entry.emailSubject}</div>Please file a Post-Summary Correction (PSC) for Entry ${entry.id} based on the following findings:<br><br>${emailBodySnippets}Please amend the entry and confirm upon transmission.`;
  };

  const selectedEntry = selectedEntryIndex !== null ? entries[selectedEntryIndex] : null;
  const emailContent = selectedEntry ? getEmailContent(selectedEntry) : null;

  const handleCopy = () => {
    if (emailRef.current) {
      const text = emailRef.current.innerText;
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopyLabel('Copied!');
    setTimeout(() => setCopyLabel('Copy to Clipboard'), 2000);
  };

  return (
    <Box className="td-resolution-wrapper">
      <Typography sx={{ marginTop: 0, marginBottom: '8px', fontWeight: 700, fontSize: '24px', color: '#1e293b' }}>
        Broker Resolution Desk
      </Typography>
      <Typography sx={{ color: '#64748b', fontSize: '14px', marginBottom: '24px', maxWidth: '800px' }}>
        Select an entry from the QA Radar to generate the exact Post-Summary Correction (PSC) instructions
        for your broker.
      </Typography>

      <Box sx={{ display: 'flex', gap: '24px' }}>
        {/* Left: Actionable PSCs queue */}
        <Box className="td-res-queue-panel">
          <Box className="td-res-queue-header">Actionable PSCs</Box>
          {entriesWithExceptions.length === 0 ? (
            <Typography sx={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
              No actionable PSCs. Resolve entries in the 19 CFR Audit Radar first.
            </Typography>
          ) : (
            entriesWithExceptions.map((e) => (
              <Box
                key={e.id}
                className={`td-res-queue-item${selectedEntryIndex === e.originalIndex ? ' td-res-selected' : ''}`}
                onClick={() => handleSelectEntry(e.originalIndex)}
              >
                ⚠️ {e.id}
                <br />
                <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#64748b' }}>
                  {e.broker}
                </span>
              </Box>
            ))
          )}
        </Box>

        {/* Right: Email / PSC content */}
        <Box sx={{ flex: 1 }}>
          <Box
            className="td-email-box"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontSize: 13, lineHeight: 1.6, position: 'relative' }}
          >
            <button className="td-copy-btn" onClick={handleCopy}>
              {copyLabel}
            </button>
            <Box ref={emailRef}>
              {selectedEntry === null ? (
                <em style={{ color: '#64748b' }}>Select an entry from the left panel to generate PSC instructions.</em>
              ) : emailContent === null ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <em>
                    <span style={{ color: '#10b981', fontSize: 18, fontWeight: 'bold' }}>✓</span>
                    <br /><br />
                    No PSC required for this entry. All compliance vectors successfully validated, or anomalies overridden as False Positives.
                  </em>
                </div>
              ) : (
                // emailContent built from our own static data — not user input
                // eslint-disable-next-line react/no-danger
                <span dangerouslySetInnerHTML={{ __html: emailContent }} />
              )}
            </Box>
          </Box>

          {emailContent && (
            <Box sx={{ marginTop: '24px', textAlign: 'right' }}>
              <button className="td-primary-btn td-btn-green">
                ⬇ Download PSC Extract (.CSV)
              </button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BrokerResolutionDesk;
